
import React, { Suspense } from 'react';
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminProvider } from "@/contexts/AdminContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

// Lazy load components for better performance
const Index = React.lazy(() => import("./pages/Index"));
const LandingPage = React.lazy(() => import("./pages/LandingPage").then(module => ({ default: module.LandingPage })));
const Login = React.lazy(() => import("./pages/auth/Login"));
const ResetPassword = React.lazy(() => import("./pages/auth/ResetPassword"));
const Onboarding = React.lazy(() => import("./pages/auth/Onboarding"));
const Unauthorized = React.lazy(() => import("./pages/Unauthorized"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

// Admin pages - lazy loaded
const Dashboard = React.lazy(() => import("./pages/admin/Dashboard").then(module => ({ default: module.Dashboard })));
const Products = React.lazy(() => import("./pages/admin/Products").then(module => ({ default: module.Products })));
const Categories = React.lazy(() => import("./pages/admin/Categories").then(module => ({ default: module.Categories })));
const Tags = React.lazy(() => import("./pages/admin/Tags").then(module => ({ default: module.Tags })));
const Complements = React.lazy(() => import("./pages/admin/Complements").then(module => ({ default: module.Complements })));
const Subscription = React.lazy(() => import("./pages/admin/Subscription").then(module => ({ default: module.Subscription })));
const Company = React.lazy(() => import("./pages/admin/Company").then(module => ({ default: module.Company })));
const Theme = React.lazy(() => import("./pages/admin/Theme").then(module => ({ default: module.Theme })));
const QRCode = React.lazy(() => import("./pages/admin/QRCode").then(module => ({ default: module.QRCode })));
const SmartImport = React.lazy(() => import("./pages/admin/SmartImport").then(module => ({ default: module.SmartImport })));
const LegacyAdminRedirect = React.lazy(() => import("./pages/admin/LegacyAdminRedirect").then(module => ({ default: module.LegacyAdminRedirect })));
const ProductCreate = React.lazy(() => import("./pages/admin/ProductCreate").then(module => ({ default: module.ProductCreate })));
const ProductEdit = React.lazy(() => import("./pages/admin/ProductEdit").then(module => ({ default: module.ProductEdit })));
const AdminLayout = React.lazy(() => import("./components/admin/AdminLayout").then(module => ({ default: module.AdminLayout })));
const SuperAdminLayout = React.lazy(() => import("./layouts/SuperAdminLayout").then(module => ({ default: module.SuperAdminLayout })));
const SuperAdminRoutes = React.lazy(() => import("./components/super-admin/SuperAdminRoutes").then(module => ({ default: module.SuperAdminRoutes })));

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner />
        <PWAInstallPrompt />
        <PWAUpdatePrompt />
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<LoadingSpinner text="Carregando AliMenu..." />}>
              <Routes>
              {/* Public routes - ordem específica para evitar conflitos */}
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              } />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/" element={<LandingPage />} />
              
              {/* Super Admin routes */}
              <Route path="/super-admin/*" element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <SuperAdminLayout />
                </ProtectedRoute>
              } />

              {/* Company-specific routes */}
              <Route path="/:companySlug" element={
                <TenantProvider>
                  <Index />
                </TenantProvider>
              } />
              
               {/* Admin routes for companies */}
               <Route path="/:companySlug/admin" element={
                 <TenantProvider>
                   <ProtectedRoute allowedRoles={['company_admin', 'super_admin']}>
                     <AdminProvider>
                       <AdminLayout />
                     </AdminProvider>
                   </ProtectedRoute>
                 </TenantProvider>
               }>
                    <Route index element={<Dashboard />} />
                    <Route path="importacao-ia" element={<SmartImport />} />
                    <Route path="produtos" element={<Products />} />
                    <Route path="produtos/novo" element={<ProductCreate />} />
                    <Route path="produtos/editar/:id" element={<ProductEdit />} />
                    <Route path="categorias" element={<Categories />} />
                    <Route path="tags" element={<Tags />} />
                    <Route path="complementos" element={<Complements />} />
                    <Route path="meu-plano" element={<Subscription />} />
                    <Route path="empresa" element={<Company />} />
                    <Route path="tema" element={<Theme />} />
                    <Route path="qrcode" element={<QRCode />} />
               </Route>

               {/* Legacy admin redirect routes */}
               <Route path="/admin/*" element={<LegacyAdminRedirect />} />

                {/* 404 route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;

